import { Check, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { resolveLocale } from '@/i18n';
import { cn } from '@/lib/utils';

import type { FieldBaseProps } from './types';

type CountryOption = { value: string; label: string };

type Props<T extends FieldValues> = FieldBaseProps<T> & {
  countries: readonly string[];
  placeholder?: string;
  /** Country code → name of the group that already owns it; rendered disabled in the list. */
  disabledCountries?: Record<string, string>;
};

function buildOptions(codes: readonly string[], locale: string): CountryOption[] {
  let displayNames: Intl.DisplayNames | null = null;
  try {
    displayNames = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
  }
  return codes
    .map((code) => ({ value: code, label: displayNames?.of(code) ?? code }))
    .sort((a, b) => a.label.localeCompare(b.label, locale));
}

export function FormMultiCountryField<T extends FieldValues>({
  name,
  label,
  description,
  required,
  disabled,
  className,
  countries,
  placeholder,
  disabledCountries,
}: Props<T>) {
  const { control } = useFormContext<T>();
  const { t, i18n } = useTranslation('common');
  const locale = resolveLocale(i18n.language);
  const options = useMemo(() => buildOptions(countries, locale), [countries, locale]);
  const [open, setOpen] = useState(false);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selected: string[] = Array.isArray(field.value) ? field.value : [];
        const toggle = (value: string) => {
          const next = selected.includes(value)
            ? selected.filter((v) => v !== value)
            : [...selected, value];
          field.onChange(next);
        };
        const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v;
        return (
          <FormItem className={className}>
            {label ? (
              <FormLabel className="text-sm font-medium leading-none text-black">
                {label}
                {required ? <span aria-hidden="true"> *</span> : null}
              </FormLabel>
            ) : null}
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild disabled={disabled}>
                <FormControl>
                  {/* Figma input group: chips inside an input-look field + attached search square. */}
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className="flex w-full items-stretch text-left disabled:opacity-50"
                  >
                    <span className="flex min-h-9 flex-1 flex-wrap items-center gap-2 rounded-l-md border border-r-0 border-input bg-white px-3 py-1">
                      {selected.length === 0 ? (
                        <span className="text-sm text-muted-foreground">
                          {t('countryPicker.selectedNone')}
                        </span>
                      ) : (
                        selected.map((code) => (
                          <span
                            key={code}
                            className="flex h-6 items-center justify-center rounded-md bg-accent px-2 text-sm font-medium text-foreground"
                          >
                            {labelFor(code)}
                          </span>
                        ))
                      )}
                    </span>
                    <span className="flex w-9 shrink-0 items-center justify-center rounded-r-md border border-input bg-white">
                      <Search className="size-4" />
                    </span>
                  </button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                className="w-[--radix-popover-trigger-width] min-w-[16rem] p-0"
                align="start"
              >
                <Command>
                  <CommandInput placeholder={placeholder ?? t('countryPicker.placeholder')} />
                  <CommandList>
                    <CommandEmpty>{t('countryPicker.empty')}</CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => {
                        const isChecked = selected.includes(option.value);
                        const takenBy = disabledCountries?.[option.value];
                        return (
                          <CommandItem
                            key={option.value}
                            value={`${option.label} ${option.value}`}
                            disabled={Boolean(takenBy)}
                            onSelect={() => {
                              if (!takenBy) toggle(option.value);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                isChecked ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            {option.label}
                            <span className="ml-auto text-xs text-muted-foreground">
                              {takenBy
                                ? t('countryPicker.alreadyIn', { market: takenBy })
                                : option.value}
                            </span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {description ? <FormDescription>{description}</FormDescription> : null}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
